import i18n from 'i18n';
import { createColumnHelper, type CellContext, type HeaderContext } from '@tanstack/react-table';
import _ from 'lodash';

// Import components
import { PriceCell } from 'components/views/offer_configurator/components/common/PaymentTable/styles';

// Import types
import type { SummaryProduct } from 'types/views/configurator';
import { PacketSubtype } from 'types/api/products/product';

// Import helpers
import { convertPriceToReadableFormat } from 'helpers/prices';
import { convertDaysToMonths } from 'helpers/time';

type Cell = CellContext<SummaryProduct, number>;
type Header = HeaderContext<SummaryProduct, number>;

const columnHelper = createColumnHelper<SummaryProduct>();

const renderPriceCell = ({ getValue, column, row }: Cell) => {
	const value = getValue();
	const price = convertPriceToReadableFormat(value);

	const isPromotion = column.id === 'current_price' && Boolean(row.original.promotion);

	return <PriceCell isPromotion={isPromotion}>{i18n.t('configurator:price', { price })}</PriceCell>;
};

const renderPriceFooter = ({ column, table }: Header) => {
	const state = table.getState();
	const isPromotion = column.id === 'current_price' && state.columnVisibility.promo_days_period;
	const sum = _.sumBy(table.options.data, column.id);
	const price = convertPriceToReadableFormat(sum);
	return <PriceCell isPromotion={isPromotion}>{i18n.t('configurator:price', { price })}</PriceCell>;
};

export const columns = (isPromotion: boolean) => [
	columnHelper.accessor('title', {
		header: () => null,
		footer: () => i18n.t('configurator:pricing_table_total'),
		cell: ({ getValue, row }) => {
			const title = getValue();
			return row.original.subtype === PacketSubtype.Main ? i18n.t('configurator:offer_packet_main_title', { title }) : title;
		},
		size: 300
	}),
	columnHelper.accessor('promotion.promo_days_period', {
		id: 'promo_days_period',
		header: () => i18n.t('configurator:pricing_table_duration'),
		cell: ({ getValue }) => {
			const days = getValue();
			const promotionDuration = convertDaysToMonths(days, i18n.language);
			return promotionDuration || i18n.t('configurator:no_promotion');
		}
	}),
	columnHelper.accessor('current_price', {
		header: () => i18n.t('configurator:pricing_table_current'),
		footer: renderPriceFooter,
		cell: renderPriceCell,
		size: 200
	}),
	columnHelper.accessor('standard_price', {
		header: () => (isPromotion ? i18n.t('configurator:pricing_table_final_promo') : i18n.t('configurator:pricing_table_final')),
		size: isPromotion ? 250 : 400,
		footer: renderPriceFooter,
		cell: renderPriceCell
	})
];
